import { useMemo, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useDrives, useWeather, useTides } from '../hooks/data.js';
import { scoreActivityAt } from '../lib/scoring.js';
import ConditionScore from '../components/ConditionScore.jsx';

// A drive's waypoints each get scored as mini-activities. The drive's
// own score is the peak of its waypoint scores at the current moment.
const TAG_TO_LIGHTING = {
  scenic: 'soft_overcast_or_golden',
  coastal: 'any',
  glacier_views: 'clear_sun',
  iconic: 'clear_sun',
  quiet: 'soft_overcast',
};

export default function Drives() {
  const { tripId, trip } = useTrip();
  const drives = useDrives(tripId);
  const lat = trip?.destination?.lat ?? 58.3019;
  const lng = trip?.destination?.lng ?? -134.4197;
  const weather = useWeather({ lat, lng });
  const tides = useTides();
  const [openId, setOpenId] = useState(null);

  const enriched = useMemo(() => {
    if (!drives.data?.drives) return [];
    const now = new Date();
    const w = currentHour(weather.data, now);
    const tide = currentTide(tides.data, now);

    return drives.data.drives.map((drive) => {
      const lighting = pickLighting(drive.tags);
      const wpScores = (drive.waypoints || []).map((wp) => {
        const synth = {
          ...wp,
          type: 'waypoint',
          lighting_preference: lighting,
          tide_sensitive: drive.id === 'out_the_road' && wp.id === 'eagle_beach',
          elevation_subject: false,
          tags: drive.tags,
        };
        const score =
          w &&
          scoreActivityAt({
            activity: synth,
            when: now,
            weather: w,
            tideSample: tide,
            originLat: wp.lat,
            originLng: wp.lng,
          });
        return { ...wp, score };
      });
      const peak = wpScores.reduce(
        (best, wp) => (wp.score && wp.score.score > (best?.score?.score ?? -1) ? wp : best),
        null
      );
      return { ...drive, waypoints: wpScores, peak };
    });
  }, [drives.data, weather.data, tides.data]);

  const open = enriched.find((d) => d.id === openId) || null;

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          scenic routes
        </p>
        <h1 className="mt-1 font-serif text-2xl">Drives</h1>
      </header>

      {drives.loading && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …loading drives
        </p>
      )}

      <div className="space-y-2">
        {enriched.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setOpenId(d.id)}
            className="card flex w-full flex-col gap-2 px-4 py-3 text-left transition hover:bg-bg-tertiary"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  {d.length_miles} mi · {d.round_trip_minutes} min round trip
                </p>
                <h3 className="font-medium">{d.name}</h3>
                <p className="truncate text-xs text-fg-secondary">{d.subtitle}</p>
              </div>
              {d.peak?.score && <ConditionScore score={d.peak.score.score} label="peak" />}
            </div>
            <p className="line-clamp-2 text-xs text-fg-secondary">{d.summary}</p>
          </button>
        ))}
      </div>

      {open && <DriveDetail drive={open} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function DriveDetail({ drive, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40" onClick={onClose}>
      <div
        className="card w-full max-w-screen-sm overflow-hidden rounded-b-none bg-bg-primary"
        style={{ paddingBottom: 'var(--safe-bottom)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border-tertiary px-4 py-3">
          <div>
            <h2 className="font-medium">{drive.name}</h2>
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              {drive.length_miles} mi · {drive.round_trip_minutes} min round trip
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-fg-tertiary hover:bg-bg-tertiary hover:text-fg-primary"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-4">
          <p className="text-sm text-fg-primary">{drive.summary}</p>

          <p className="mt-4 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            photo waypoints ({drive.waypoints.length})
          </p>
          <ul className="mt-2 space-y-2">
            {drive.waypoints.map((wp) => (
              <li key={wp.id} className="card flex flex-col gap-1 px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{wp.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                      mile {wp.from_start_miles} · {wp.best_window?.replace(/_/g, ' ')}
                    </p>
                  </div>
                  {wp.score?.score != null && <ConditionScore score={wp.score.score} size="sm" label="" />}
                </div>
                {wp.subject && <p className="text-xs text-fg-secondary">{wp.subject}</p>}
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${wp.lat},${wp.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="self-start font-mono text-[10px] uppercase tracking-wider text-action transition hover:underline"
                >
                  Directions
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function pickLighting(tags = []) {
  for (const t of tags) {
    if (TAG_TO_LIGHTING[t]) return TAG_TO_LIGHTING[t];
  }
  return 'any';
}

function currentHour(weatherData, now) {
  if (!weatherData?.hourly?.time?.length) return null;
  const ms = now.getTime();
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < weatherData.hourly.time.length; i += 1) {
    const t = new Date(weatherData.hourly.time[i]).getTime();
    const diff = Math.abs(ms - t);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  const out = {};
  for (const k of Object.keys(weatherData.hourly)) {
    if (Array.isArray(weatherData.hourly[k])) out[k] = weatherData.hourly[k][bestIdx];
  }
  return out;
}

function currentTide(tideData, now) {
  if (!tideData?.series?.length) return null;
  const ms = now.getTime();
  let best = tideData.series[0];
  let bestDiff = Infinity;
  for (const row of tideData.series) {
    const diff = Math.abs(ms - new Date(row.t).getTime());
    if (diff < bestDiff) {
      bestDiff = diff;
      best = row;
    }
  }
  return best;
}
